import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bot, Megaphone, ClipboardList, Store, Phone, ArrowRight, Sparkles } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { api, fmtINR, fmtNum, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LeafLoader, LeafShape, PageHeader, StatusPill, Waveform } from "@/components/bits";

const COLORS = ["#22C55E", "#F59E0B", "#06B6D4", "#EA580C", "#10B981"];

function useCount(n) {
  const [v, setV] = useState(0);
  useEffect(() => {
    const target = Number(n) || 0;
    const start = performance.now();
    const from = 0;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / 900);
      setV(Math.round(from + (target - from) * (1 - (1 - p) ** 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [n]);
  return v;
}

function LiveStat({ label, value, format = fmtNum, sub, accent = "emerald", testId, icon: Icon, delay = 0 }) {
  const n = useCount(value);
  const colors = { emerald: "text-emerald-300", gold: "text-amber-300", cyan: "text-cyan-300", slate: "text-slate-100" };
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className="glass p-5 relative overflow-hidden"
      data-testid={testId}
    >
      <div className="absolute -right-6 -top-8 w-24 h-24 rounded-full bg-emerald-500/10 blur-2xl pointer-events-none" />
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">{label}</p>
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
      </div>
      <p className={`font-display text-2xl sm:text-3xl font-bold ${colors[accent]}`}>{format(n)}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </motion.div>
  );
}

export default function Home() {
  const { company } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => setD({})); }, []);

  const steps = [
    { done: (d?.retailers || 0) > 0, t: "Add your retailer network", to: "/dashboard/retailers", icon: Store },
    { done: (d?.agents || 0) > 0, t: "Build your first agent", to: "/dashboard/agents/new", icon: Bot },
    { done: (d?.campaigns || 0) > 0, t: "Run a campaign", to: "/dashboard/campaigns", icon: Megaphone },
    { done: (d?.orders || 0) > 0, t: "Convert a PO to SO", to: "/dashboard/orders", icon: ClipboardList },
  ];

  if (!d) {
    return (
      <div className="glass p-10">
        <LeafLoader label="Gathering your value chain…" />
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute -top-16 -left-20 w-72 h-72 rounded-full bg-emerald-600/10 blur-[90px] pointer-events-none" />
      <div className="absolute top-24 right-0 w-56 h-56 rounded-full bg-amber-500/10 blur-[90px] pointer-events-none" />
      <PageHeader overline={company?.name} title={`Good day${company ? "," : ""} here's your value chain`} desc="Live view across agents, campaigns, conversations and orders." testId="home-header"
        action={<Link to="/dashboard/campaigns" className="btn-primary" data-testid="home-new-campaign-btn"><Megaphone className="w-4 h-4" /> New campaign</Link>} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <LiveStat label="Agents" value={d.agents} sub={`${d.campaigns || 0} campaigns`} accent="gold" testId="stat-agents" delay={0.05} />
        <LiveStat label="Conversations" value={d.conversations} sub={`${d.pickup_rate || 0}% pick-up rate`} testId="stat-conversations" icon={Phone} delay={0.12} />
        <LiveStat label="POs raised" value={d.orders} sub={fmtINR(d.po_value) + " value"} accent="cyan" testId="stat-orders" delay={0.19} />
        <LiveStat label="Retailers" value={d.retailers} sub={`${d.agents || 0} agents · ${d.campaigns || 0} campaigns`} accent="slate" testId="stat-retailers" delay={0.26} />
      </div>

      <div className="grid lg:grid-cols-12 gap-5 mt-5">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-7 glass p-6" data-testid="home-recent-conversations">
          <div className="flex items-center justify-between mb-4"><h3 className="font-display font-semibold text-slate-100">Recent conversations</h3><Link to="/dashboard/conversations" className="text-xs text-emerald-300 hover:text-emerald-200 flex items-center gap-1">All <ArrowRight className="w-3 h-3" /></Link></div>
          {d.recent_conversations?.length ? d.recent_conversations.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 + i * 0.06 }}>
              <Link to={`/dashboard/conversations/${c.id}`} className="flex items-center gap-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/[0.02] -mx-2 px-2 rounded-lg" data-testid={`recent-conv-${c.id}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${c.audience === "farmer" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{c.contact?.name?.[0] || "?"}</div>
                <div className="flex-1 min-w-0"><p className="text-sm text-slate-100 truncate">{c.contact?.name} <span className="text-slate-500">· {c.district || c.state}</span></p><p className="text-xs text-slate-500 truncate">{c.summary}</p></div>
                <div className="text-right shrink-0"><StatusPill status={c.outcome} /><p className="text-[11px] text-slate-600 mt-1">{timeAgo(c.created_at)}</p></div>
              </Link>
            </motion.div>
          )) : (
            <div className="py-10 text-center">
              <Waveform bars={13} className="justify-center mb-4" color="bg-slate-600" />
              <p className="text-sm text-slate-400">No conversations yet. Finish setup to launch your first campaign.</p>
            </div>
          )}
        </motion.div>
        <div className="lg:col-span-5 space-y-5">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }} className="glass p-6 relative overflow-hidden" data-testid="home-setup-checklist">
            <svg className="pointer-events-none absolute left-5 top-16 bottom-5 w-8" viewBox="0 0 32 220" preserveAspectRatio="none" aria-hidden>
              <path className="branch-draw" d="M16 4 C12 50 22 90 16 130 C10 170 20 200 16 216" fill="none" stroke="#166534" strokeWidth="2.4" strokeLinecap="round" />
            </svg>
            <div className="flex items-center gap-2 mb-5"><Sparkles className="w-4 h-4 text-amber-300" /><h3 className="font-display font-semibold text-slate-100">Get to first order</h3></div>
            <div className="space-y-3 pl-8">
              {steps.map((s, i) => (
                <motion.div
                  key={s.t}
                  initial={{ opacity: 0, x: -16, rotate: -6, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, rotate: 0, scale: 1 }}
                  transition={{ delay: 0.34 + i * 0.14, type: "spring", stiffness: 140, damping: 16 }}
                >
                  <Link to={s.to} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${s.done ? "border-emerald-500/20 bg-emerald-500/5" : "border-white/10 hover:border-white/20"}`} data-testid={`setup-step-${s.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <LeafShape className={`w-4 h-5 shrink-0 ${s.done ? "text-emerald-400" : "text-emerald-700"}`} />
                    <s.icon className={`w-4 h-4 ${s.done ? "text-emerald-300" : "text-slate-400"}`} />
                    <span className={`text-sm flex-1 ${s.done ? "text-slate-400 line-through" : "text-slate-200"}`}>{s.t}</span>
                    {!s.done && <ArrowRight className="w-3.5 h-3.5 text-slate-500" />}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.36 }} className="glass p-6 grid grid-cols-2 gap-4">
            <div className="min-w-0"><p className="text-[11px] font-mono uppercase text-slate-500 mb-2">Outcomes</p>
              <div style={{ height: 112, minWidth: 0 }}>{d.outcomes?.length ? <ResponsiveContainer width="100%" height={112}><PieChart><Pie data={d.outcomes} dataKey="value" innerRadius={28} outerRadius={48} paddingAngle={3}>{d.outcomes.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip contentStyle={{ background: "#0F1612", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} /></PieChart></ResponsiveContainer> : <p className="text-xs text-slate-600">—</p>}</div></div>
            <div className="min-w-0"><p className="text-[11px] font-mono uppercase text-slate-500 mb-2">By state</p>
              <div style={{ height: 112, minWidth: 0 }}>{d.by_state?.length ? <ResponsiveContainer width="100%" height={112}><BarChart data={d.by_state.slice(0, 4)} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}><XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 9 }} axisLine={false} tickLine={false} /><YAxis hide /><Tooltip contentStyle={{ background: "#0F1612", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} /><Bar dataKey="value" fill="#F59E0B" radius={[4, 4, 0, 0]} maxBarSize={36} /></BarChart></ResponsiveContainer> : <p className="text-xs text-slate-600">—</p>}</div></div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
